import { useState } from "react";
import "./AddSubscription.scss";
import {
  projectFirestore,
  projectStorage,
  timestamp,
} from "../../firebase-config/config";
import { useCollection } from "../../firebase-files/hooks/useCollection";
import { useAuthContext } from "../../firebase-files/hooks/useAuthContext";
import Select from "react-select";

const AddSubscription = ({ isModalOpen, closeModal }) => {
  const { user } = useAuthContext();

  // Fetch subscription category and sub category
  const { documents: subscriptionCategories } = useCollection("m_categories", [
    "source",
    "==",
    "subscription",
  ]);

  const [isLoading, setIsLoading] = useState(false);

  // add new subscription functionality
  const [newSubscription, setNewSubscription] = useState({
    name: "",
    subscriptionDuration: "",
    subscriptionDurationType: "week",
    shortDescription: "",
    subscriptionFreeTrail: "",
    price: "",
    priceType: "Fixed",
    category: [],
    subCategory: [],
    images: [],
    buyerType: "",
    productshipTo: [],
    CustomProductshipUrl: "",
  });
  const [imageFiles, setImageFiles] = useState([]);
  const [imageUploadProgress, setImageUploadProgress] = useState(0);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewSubscription((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e, index) => {
    const file = e.target.files[0];
    if (file) {
      setImageFiles((prevFiles) => {
        const newFiles = [...prevFiles];
        newFiles[index] = file;
        return newFiles;
      });
    }
  };

  const handleRemoveImage = (index) => {
    setImageFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const getCategoryOptions = () => {
    return (
      subscriptionCategories
        ?.filter((cat) => cat.status === "active")
        .map((cat) => ({
          value: cat.category,
          label: cat.category,
        })) || []
    );
  };

  const getSubCategoryOptions = () => {
    const selectedCategories = Array.isArray(newSubscription.category)
      ? newSubscription.category
      : [newSubscription.category];
    let subCategoryOptions = [];

    selectedCategories.forEach((categoryValue) => {
      const categoryDoc = subscriptionCategories?.find(
        (cat) => cat.category === categoryValue
      );
      if (categoryDoc?.subCategories) {
        const subCats = categoryDoc.subCategories
          .filter((subCat) => subCat.status === "active")
          .map((subCat) => ({
            value: subCat.subCategoryName,
            label: subCat.subCategoryName,
            category: categoryValue,
          }));
        subCategoryOptions = [...subCategoryOptions, ...subCats];
      }
    });

    return subCategoryOptions;
  };

  const handleCategorySelection = (selectedOptions) => {
    const selectedCategories = selectedOptions
      ? selectedOptions.map((option) => option.value)
      : [];
    setNewSubscription((prev) => ({
      ...prev,
      category: selectedCategories,
      subCategory: prev.subCategory.filter((subCat) => {
        const subCatOption = getSubCategoryOptions().find(
          (opt) => opt.value === subCat
        );
        return (
          subCatOption && selectedCategories.includes(subCatOption.category)
        );
      }),
    }));
  };

  const handleSubCategorySelection = (selectedOptions) => {
    setNewSubscription((prev) => ({
      ...prev,
      subCategory: selectedOptions
        ? selectedOptions.map((option) => option.value)
        : [],
    }));
  };

  const handleAddNewSubscription = async (e) => {
    e.preventDefault();

    if (imageFiles.length === 0) {
      // Optionally show a snackbar or alert here
      return;
    }

    setIsLoading(true);

    try {
      // Add the subscription to Firestore to get the subscription ID
      const subscriptionRef = await projectFirestore
        .collection("products")
        .add({
          name: newSubscription.name,
          productType: "subscription",
          short_description: newSubscription.shortDescription,
          price: newSubscription.price,
          subscriptionDuration: newSubscription.subscriptionDuration,
          subscriptionDurationType: newSubscription.subscriptionDurationType,
          subscriptionFreeTrail: newSubscription.subscriptionFreeTrail,
          priceType: newSubscription.priceType || "Fixed",
          discount: 0,
          productLocation: "",
          rating: "4.5",
          order: 0,
          external_url: "",
          features: "Add Features",
          description: "No description available",
          status: "inactive",
          createdAt: timestamp.fromDate(new Date()),
          images: [],
          category: newSubscription.category,
          subCategory: newSubscription.subCategory,
          buyerType: newSubscription.buyerType,
          productshipTo: newSubscription.productshipTo,
          customProductshipUrl: newSubscription.CustomProductshipUrl || "",
          newArrivals: false,
          popularProducts: false,
          topSellingProducts: false,
          city: "",
          isShippingAllow: false,
          isSubscription: true,
          productId: "",
          gst: "",
          cgst: "",
          sgst: "",
          cess: "",
        });

      const subscriptionId = subscriptionRef.id;

      // Upload images to the subscription-specific folder
      const imageUrls = await Promise.all(
        imageFiles.map((file) => uploadImage(file, subscriptionId))
      );

      // Update the subscription document with the image URLs
      await subscriptionRef.update({ images: imageUrls });

      // Get current category prefix
      const categoryList = newSubscription.category;
      const categoryPrefix =
        Array.isArray(categoryList) && categoryList.length > 0
          ? categoryList[0].substring(0, 3).toUpperCase()
          : "GEN";

      // Get date parts
      const now = new Date();
      const year = String(now.getFullYear()).slice(-2);
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const day = String(now.getDate()).padStart(2, "0");

      // Fetch product count
      const countDocRef = projectFirestore
        .collection("settings")
        .doc("total_products_count");
      const countDocSnap = await countDocRef.get();

      let productCount;
      if (countDocSnap.exists) {
        const currentCount = countDocSnap.data().productsCount || 10000;
        productCount = currentCount + 1;
        await countDocRef.update({ productsCount: productCount });
      } else {
        productCount = 10001;
        await countDocRef.set({ productsCount: productCount });
      }

      // Construct productId
      const customProductId = `${categoryPrefix}-${year}${month}${day}-${productCount}`;

      // Update product with productId
      await subscriptionRef.update({
        productId: customProductId,
      });

      // Reset form and close modal
      setNewSubscription({
        name: "",
        shortDescription: "",
        price: "",
        subscriptionDuration: "",
        subscriptionDurationType: "week",
        subscriptionFreeTrail: "",
        priceType: "Fixed",
        category: [],
        subCategory: [],
        buyerType: "",
        productshipTo: [],
        images: [],
        CustomProductshipUrl: "",
      });
      setImageFiles([]);
      setImageUploadProgress(0);
      closeModal();
    } catch (error) {
      console.error("Error adding subscription:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const uploadImage = async (file, subscriptionId) => {
    const storageRef = projectStorage.ref(
      `subscription_img/${subscriptionId}/${file.name}`
    );
    const uploadTask = storageRef.put(file);

    return new Promise((resolve, reject) => {
      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setImageUploadProgress(progress);
        },
        (error) => reject(error),
        async () => {
          const url = await uploadTask.snapshot.ref.getDownloadURL();
          resolve(url);
        }
      );
    });
  };

  return (
    <div className="add-subscription-compo-parent-div">
      {/* ADD SUBSCRIPTION MODAL START */}
      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button className="modal-close-btn" onClick={closeModal}>
              &times;
            </button>
            <h2>Add New Product Subscription</h2>
            <form
              onSubmit={handleAddNewSubscription}
              className="add-subscription-form"
            >
              {/* Images */}
              <div className="image-upload-container">
                {[0, 1, 2, 3, 4].map((index) => (
                  <div key={index} className="image-upload-box">
                    {imageFiles[index] ? (
                      <div className="image-preview">
                        <img
                          src={URL.createObjectURL(imageFiles[index])}
                          alt={`Preview ${index}`}
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveImage(index)}
                          className="remove-image-btn"
                        >
                          X
                        </button>
                      </div>
                    ) : (
                      <>
                        <input
                          type="file"
                          id={`image-upload-${index}`}
                          accept="image/*"
                          onChange={(e) => handleImageChange(e, index)}
                          style={{ display: "none" }}
                        />
                        <label
                          htmlFor={`image-upload-${index}`}
                          className="upload-label"
                        >
                          +<span>Add Image</span>
                        </label>
                      </>
                    )}
                  </div>
                ))}
              </div>

              <input
                type="text"
                name="name"
                value={newSubscription.name}
                onChange={handleInputChange}
                placeholder="Product Name"
                required
                className="form-input"
              />

              <textarea
                name="shortDescription"
                value={newSubscription.shortDescription}
                onChange={handleInputChange}
                placeholder="Short Description"
                className="form-input"
                rows="3"
              ></textarea>

              <div className="dropdown-row">
                <div className="form-input-container">
                  <label>Categories</label>
                  <Select
                    isMulti
                    options={getCategoryOptions()}
                    value={getCategoryOptions().filter((option) =>
                      newSubscription.category.includes(option.value)
                    )}
                    onChange={handleCategorySelection}
                    className="category-multiselect"
                    classNamePrefix="category-select"
                    placeholder="Select categories..."
                  />
                </div>

                <div className="form-input-container">
                  <label>Subcategories</label>
                  <Select
                    isMulti
                    options={getSubCategoryOptions()}
                    value={getSubCategoryOptions().filter((option) =>
                      newSubscription.subCategory.includes(option.value)
                    )}
                    onChange={handleSubCategorySelection}
                    className="subcategory-multiselect"
                    classNamePrefix="subcategory-select"
                    placeholder="Select subcategories..."
                    isDisabled={newSubscription.category.length === 0}
                  />
                </div>
                <input
                  type="number"
                  name="subscriptionFreeTrail"
                  value={newSubscription.subscriptionFreeTrail}
                  onChange={handleInputChange}
                  placeholder="Free Trial In days (Optional)"
                  className="form-input"
                />
                <div className="d-flex gap-2">
                  <select
                    name="subscriptionDurationType"
                    value={newSubscription.subscriptionDurationType}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    <option value="week">Week</option>
                    <option value="month">Month</option>
                    {/* <option value="year">Year</option> */}
                  </select>
                  <input
                    type="number"
                    name="subscriptionDuration"
                    value={newSubscription.subscriptionDuration}
                    onChange={handleInputChange}
                    placeholder="Subscription Duration"
                    required
                    className="form-input"
                  />
                </div>
                <div className="d-flex gap-2">
                  <input
                    type="number"
                    name="price"
                    value={newSubscription.price}
                    onChange={handleInputChange}
                    placeholder="Price"
                    required
                    className="form-input"
                  />
                  <select
                    name="priceType"
                    value={newSubscription.priceType}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    <option value="Fixed">Fixed</option>
                    <option value="Onward">Onward</option>
                  </select>
                </div>
              </div>

              <input
                type="text"
                name="CustomProductshipUrl"
                value={newSubscription.CustomProductshipUrl}
                onChange={handleInputChange}
                placeholder="Custom URL (Optional)"
                className="form-input"
                title="This is a custom URL. Clicking the product card will redirect to this URL."
              />
              <div className="form-actions">
                <button
                  type="button"
                  onClick={closeModal}
                  className="btn-cancel"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-save" disabled={isLoading}>
                  {isLoading ? "Adding..." : "Save"}
                </button>
              </div>
            </form>
            {imageUploadProgress > 0 && (
              <p>Upload progress: {imageUploadProgress.toFixed(2)}%</p>
            )}
          </div>
        </div>
      )}
      {/* ADD SUBSCRIPTION MODAL END */}
    </div>
  );
};

export default AddSubscription;
